import { type FormEvent, useState } from 'react';
import { navigate } from '../App';
import { Sheet } from '../game/Sheet';
import { ART, pawnArt } from '../lib/art';
import { saveName, savedName } from '../lib/storage';

/** Menu principal no estilo do Business Tour: placa do jogador, faixa amarela e blocos grandes. */
export function Home() {
  const [name, setName] = useState(savedName());
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [rules, setRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanName = name.trim();

  async function create(bots = 0) {
    if (!cleanName) return setError('Escolha um apelido (lá em cima) para os amigos te reconhecerem.');
    setBusy(true);
    setError(null);
    try {
      saveName(cleanName);
      const res = await fetch('/api/rooms', { method: 'POST' });
      const data = (await res.json()) as { code?: string; error?: string };
      if (!res.ok || !data.code) throw new Error(data.error ?? 'Não deu para criar a mesa.');
      navigate(`/sala/${data.code}${bots ? `?bots=${bots}` : ''}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sem conexão. Confira a internet e tente de novo.');
      setBusy(false);
    }
  }

  function join(e: FormEvent) {
    e.preventDefault();
    if (!cleanName) return setError('Escolha um apelido (lá em cima) para os amigos te reconhecerem.');
    const c = code.trim().toUpperCase();
    if (!/^[A-Z]{4}$/.test(c)) return setError('O código da mesa tem 4 letras.');
    saveName(cleanName);
    navigate(`/sala/${c}`);
  }

  return (
    <main className="menu-screen">
      <i className="sky-squares" aria-hidden="true" />
      <header className="menu-top">
        <label className="profile">
          <span className="profile-avatar" aria-hidden="true">
            <img src={pawnArt(0).idle} alt="" />
          </span>
          <span className="profile-body">
            <span className="profile-ribbon">Seu apelido</span>
            <input
              className="profile-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              maxLength={16}
              autoComplete="nickname"
              placeholder="Como a galera te chama"
            />
          </span>
        </label>
        <div className="ribbon ribbon-yellow menu-title">
          <h1>Menu principal</h1>
        </div>
      </header>

      <img className="menu-logo" src={ART.logo} alt="Tigrinho Tour" width={900} height={598} />

      <div className="tiles">
        <section className="tile" aria-labelledby="tile-mesa">
          <div className="tile-art" aria-hidden="true">
            <Dice />
          </div>
          <div className="ribbon ribbon-blue tile-ribbon">
            <h2 id="tile-mesa">Mesa privada</h2>
          </div>
          <div className="tile-tabs">
            <button onClick={() => create()} disabled={busy}>
              {busy ? 'Criando…' : 'Criar'}
            </button>
            <button className={joining ? 'is-on' : ''} onClick={() => setJoining((j) => !j)}>
              Entrar
            </button>
          </div>
        </section>

        <section className="tile" aria-labelledby="tile-bots">
          <div className="tile-art tile-art-cast" aria-hidden="true">
            <img src={pawnArt(3).idle} alt="" />
            <img src={pawnArt(1).idle} alt="" />
            <img src={pawnArt(4).idle} alt="" />
          </div>
          <div className="ribbon ribbon-green tile-ribbon">
            <h2 id="tile-bots">Treinar com bots</h2>
          </div>
          <div className="tile-tabs">
            <button onClick={() => create(1)} disabled={busy}>
              1 bot
            </button>
            <button onClick={() => create(3)} disabled={busy}>
              3 bots
            </button>
          </div>
        </section>

        <section className="tile" aria-labelledby="tile-regras">
          <div className="tile-art" aria-hidden="true">
            <img src={ART.trophy} alt="" />
          </div>
          <div className="ribbon ribbon-pink tile-ribbon">
            <h2 id="tile-regras">Como jogar</h2>
          </div>
          <div className="tile-tabs">
            <button onClick={() => setRules(true)}>Ver regras</button>
          </div>
        </section>
      </div>

      {joining && (
        <form className="join-card" onSubmit={join}>
          <label htmlFor="code">Código da mesa</label>
          <input
            id="code"
            className="code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 4).toUpperCase())}
            placeholder="ABCD"
            autoCapitalize="characters"
            autoFocus
          />
          <button className="pill pill-blue">Entrar</button>
        </form>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      <footer className="menu-foot">De 2 a 4 jogadores · cada um no seu celular · partidas de até 15, 30 ou 60 minutos</footer>

      {rules && <RulesSheet onClose={() => setRules(false)} />}
    </main>
  );
}

function Dice() {
  return (
    <svg viewBox="0 0 120 100" className="menu-dice">
      <g transform="translate(14 30) rotate(-14 26 26)">
        <rect width="52" height="52" rx="12" fill="#e8344e" />
        <rect width="52" height="46" rx="12" fill="#ff5470" />
        {[
          [14, 13],
          [38, 13],
          [26, 24],
          [14, 35],
          [38, 35],
        ].map(([x, y], k) => (
          <circle key={k} cx={x} cy={y} r="4.6" fill="#fff" />
        ))}
      </g>
      <g transform="translate(58 8) rotate(12 26 26)">
        <rect width="52" height="52" rx="12" fill="#e8344e" />
        <rect width="52" height="46" rx="12" fill="#ff5470" />
        {[
          [14, 12],
          [38, 12],
          [14, 23],
          [38, 23],
          [14, 34],
          [38, 34],
        ].map(([x, y], k) => (
          <circle key={k} cx={x} cy={y} r="4.6" fill="#fff" />
        ))}
      </g>
    </svg>
  );
}

/** Regras em linguagem simples (iguais às do Business Tour; só as cartas são nossas). */
export function RulesSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="Como jogar" onClose={onClose} wide>
      <div className="rules">
        <section>
          <h3>O básico</h3>
          <p>Todo mundo começa com 2 milhões de contos, e quem começa é sorteado. Jogue os dados, ande pelo tabuleiro e compre cidades. Quem cair na sua cidade paga aluguel. Passou pelo Início, ganha 300 mil.</p>
        </section>
        <section>
          <h3>Bandeirola, casas e hotel</h3>
          <p>Ao comprar uma cidade, você escolhe até onde construir: só a bandeirola, casa 1, casa 2 ou casa 3. O hotel só sai se você já tem a casa 3 e cai de novo nela. Hotel ninguém toma, derruba nem interdita.</p>
        </section>
        <section>
          <h3>Tomar a cidade dos outros</h3>
          <p>Caiu na cidade de alguém? Paga o aluguel e pode comprá-la dele por 4 vezes o que foi investido. Depois da compra dá para construir até a casa 3; o hotel só sai se a cidade já estava na casa 3. Hotel e resort não podem ser tomados.</p>
        </section>
        <section>
          <h3>Cantos</h3>
          <p>Ilha: fica preso até tirar dupla (3 tentativas) ou pagar 200 mil. Copa: escolha uma cidade sua para dobrar o aluguel. Disco Voador: na próxima vez, voe para onde quiser por 50 mil.</p>
        </section>
        <section>
          <h3>Vitória na hora</h3>
          <p>Feche 3 grupos de cor, ou um lado inteiro do tabuleiro, ou os 4 resorts. Se acabar o tempo, vence o maior patrimônio. Quem não consegue pagar vende cidades ao banco pela metade; se nem assim der, faliu e está fora.</p>
        </section>
        <section>
          <h3>F total ou Legaaaal</h3>
          <p>Cair na casa da carta é sorte ou azar: Legaaaal são as boas, F total as ruins. No fim da partida entram as cartas de Caos.</p>
        </section>
        <section>
          <h3>🍀 Sorte de Malandro</h3>
          <p>Quem não sai gastando tudo (guarda em caixa pelo menos metade do que já investiu) ou é o azarão da mesa fica com o trevo aceso: tem mais chance de puxar carta boa, e de ataque.</p>
        </section>
        <section>
          <h3>🧲 Ímã do Azarão</h3>
          <p>Quem tem bem menos cidades que os outros vira o azarão. As cidades dele ganham um ímã: quem parar na casa logo antes (a Ilha não conta: o ímã pula a Ilha) joga um dado especial, e com 50% de chance é puxado para dentro e paga o aluguel.</p>
        </section>
        <section>
          <h3>♥ Faz o L</h3>
          <p>Na casa do imposto o Governo do Amor cobra 25% do valor das suas propriedades. Dá para pagar com amor ou sonegar: nos dados, 8 ou mais paga só 20% do imposto; menos que isso paga o dobro e fica 3 rodadas preso (dupla não solta, fiança de 600 mil).</p>
        </section>
        <section>
          <h3>🚩 MST</h3>
          <p>Cidade com casa que fica 12 rodadas sem ninguém pagar aluguel é invadida e perde um nível (nunca menos que a bandeirola; hotel ninguém mexe). Na hora dá para chamar o fazendeiro por 300 mil. A Carta Fazendeiro protege uma cidade para sempre.</p>
        </section>
        <section>
          <h3>★ Evento</h3>
          <p>No começo, 2 cidades são sorteadas para receber um Evento: pelo mesmo preço, cobram 1,7 vez o aluguel.</p>
        </section>
      </div>
      <div className="deal-actions">
        <button className="pill pill-blue" onClick={onClose}>
          Entendi
        </button>
      </div>
    </Sheet>
  );
}
